/**
 * Step 3's questions — before the plans run, and again under "Things that change the answer" (downsizing
 * rebuild, 21 Oct 2026; the agreed mock-up's j1-setup). The home, the price and the state are answered on
 * step 1, so five questions are left:
 *   1. why they are thinking of moving — it decides whether a dearer home is a plan or a what-if;
 *   2. the years to plan for — 10, suggested for a move, with "why 10?";
 *   3. the money kept for emergencies — kept back when money moves, drawn on only if a year runs short
 *      (owner, 20 Oct 2026; the review: "the plans assume a reserve the client never saw");
 *   4. whether they would consider a guaranteed income for life — the shared question (GuaranteedIncomeQuestions),
 *      the client's own answer; "we work out how much";
 *   5. how freed-up money would be invested.
 * The cashflow-covered goal's yearly target (review fix, 19 Oct 2026) stays, on one line under them.
 * "Build a plan yourself instead" sits beside the run button.
 */
import React, { useMemo, useState } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { GuaranteedIncomeQuestions, type GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { dzSaleFigures, dzDutyFor } from './dzSaleFigures.ts';
import { DZ_WHY_TEN } from './DzStep2.tsx';
import type { DzMotive, DzHousing } from './downsizerScenario.tsx';
import { QuestionTitle, BuildYourselfLine } from '../../components/strategy/QuestionParts.tsx';

export interface DzAnswers {
    motive: DzMotive;
    years: number;
    emergencyReserve: number;
    /** The guaranteed-income answers given on this screen, written to the CLIENT's record when the plans run; null when unchanged. */
    guaranteedIncome: GuaranteedIncomeGoals | null;
    investProfile: string;
    cashflowTarget: number;
}

const num = (x: unknown, d = 0) => (x != null && x !== '' && Number.isFinite(Number(x)) ? Number(x) : d);

export function dzAnswersFromGoals(factFind: DraftFactFind): DzAnswers {
    const g = (factFind?.goals || {}) as Record<string, unknown>;
    return {
        motive: g.dzMotive === 'money' || g.dzMotive === 'home' ? g.dzMotive : 'both',
        years: Math.min(30, Math.max(1, num(g.dzLegacyYears, 10))),
        emergencyReserve: Math.max(0, num(g.dzEmergencyReserve, getRecommendedEmergencyFund(factFind))),
        guaranteedIncome: null,
        investProfile: String(g.newMoneyInvestmentProfile || ''),
        // ONE RULE FOR "COVERS THE COSTS" (owner, 23 Oct 2026): money in at least matches money out, as aged care has always used; no module asks for a margin.
        cashflowTarget: 0,
    };
}

/** The answers as they are kept on the file. The guaranteed-income answers are the CLIENT's: only what was answered here is written. */
export function dzAnswersToGoals(a: DzAnswers): Record<string, unknown> {
    return {
        dzMotive: a.motive,
        dzLegacyYears: a.years,
        dzEmergencyReserve: a.emergencyReserve,
        dzCashflowTarget: a.cashflowTarget,
        ...(a.guaranteedIncome || {}),
        ...(a.investProfile ? { newMoneyInvestmentProfile: a.investProfile } : {}),
    };
}

/** What the plans would invest: the money freed once the home and its duty are paid, less what tops up the reserve. */
export function dzInvestable(factFind: DraftFactFind, sale: { salePrice: number; sellingCosts: number }, housing: DzHousing, newHomePrice: number, state: string, reserve: number): number {
    const anchor = newHomePrice > 0 ? newHomePrice : Math.round(sale.salePrice * 0.7);
    const buying = dzSaleFigures({ salePrice: sale.salePrice, sellingCosts: sale.sellingCosts, newHomePrice: anchor, stampDuty: dzDutyFor(state, anchor), sellAndRent: false });
    const freed = housing === 'buy' ? buying.freedCapital : buying.netProceeds;
    const bank = (factFind?.assets?.bankAccounts || []).reduce((s, a) => s + (parseFloat(String((a as { value?: unknown })?.value)) || 0), 0);
    return Math.max(0, freed - Math.max(0, reserve - bank));
}

interface DzQuestionsProps {
    factFind: DraftFactFind;
    sale: { salePrice: number; sellingCosts: number };
    housing: DzHousing;
    newHomePrice: number;
    state: string;
    firstRun: boolean;
    onRun: (answers: DzAnswers) => void;
    onBuildYourOwn: () => void;
    onCancel?: () => void;
}

const link = 'font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy';

const Why = ({ open, onToggle, children }: { open: boolean; onToggle: () => void; children: React.ReactNode }) => (
    <>
        {' '}<button type="button" onClick={onToggle} aria-expanded={open} className="text-[12px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2">what&apos;s this?</button>
        {open && <span className="mt-1.5 block rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-[13px] font-normal leading-relaxed text-slate-700">{children}</span>}
    </>
);

const Q = ({ n, title, why, whyOpen = false, onWhy, children, testId }: { n: number; title: React.ReactNode; why?: React.ReactNode; whyOpen?: boolean; onWhy?: () => void; children: React.ReactNode; testId?: string }) => (
    <div className="border-t border-slate-200 pt-4" data-testid={testId}>
        <QuestionTitle n={n}>{title}{why && onWhy && <Why open={whyOpen} onToggle={onWhy}>{why}</Why>}</QuestionTitle>
        <div className="mt-2">{children}</div>
    </div>
);

const Radio = ({ name, checked, onChange, children, testId }: { name: string; checked: boolean; onChange: () => void; children: React.ReactNode; testId?: string }) => (
    <label className="flex cursor-pointer items-start gap-2 py-1 text-[14px] text-slate-700">
        <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1 accent-blue-600" data-testid={testId} />
        <span>{children}</span>
    </label>
);

const DzQuestions = ({ factFind, sale, housing, newHomePrice, state, firstRun, onRun, onBuildYourOwn, onCancel }: DzQuestionsProps) => {
    const v = householdVoice(factFind);
    const [a, setA] = useState<DzAnswers>(() => dzAnswersFromGoals(factFind));
    const set = (patch: Partial<DzAnswers>) => setA((prev) => ({ ...prev, ...patch }));
    const [openWhy, setOpenWhy] = useState<string | null>(null);
    const whyProps = (id: string) => ({ whyOpen: openWhy === id, onWhy: () => setOpenWhy((o) => (o === id ? null : id)) });
    const suggestedReserve = getRecommendedEmergencyFund(factFind);
    const investable = dzInvestable(factFind, sale, housing, newHomePrice, state, a.emergencyReserve);
    const giView = useMemo(() => (a.guaranteedIncome
        ? ({ ...factFind, goals: { ...((factFind?.goals || {}) as Record<string, unknown>), ...a.guaranteedIncome } } as DraftFactFind)
        : factFind), [factFind, a.guaranteedIncome]);
    const appetiteNone = String(((giView?.goals || {}) as { annuityAppetite?: string }).annuityAppetite || '') === 'none';

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="dz-questions">
            <h2 className="text-[22px] font-bold text-brand-navy">Five questions before we test the ways to use the money</h2>
            <p className="mt-1 text-[15px] text-slate-600">The sale, the new home and the state are on step 1; everything else comes from {v.possessive} file. Our suggestions are filled in, and each can be changed later.</p>
            <BuildYourselfLine onClick={onBuildYourOwn} testId="dz-build-own-top" />

            <div className="mt-4 space-y-4">
                <Q n={1} title={`Why ${v.are} ${v.names} thinking of moving?`} testId="dz-q-motive">
                    <Radio name="dz-motive" checked={a.motive === 'money'} onChange={() => set({ motive: 'money' })} testId="dz-motive-money">To free up money</Radio>
                    <Radio name="dz-motive" checked={a.motive === 'home'} onChange={() => set({ motive: 'home' })} testId="dz-motive-home">For a home that suits {v.them} better</Radio>
                    <Radio name="dz-motive" checked={a.motive === 'both'} onChange={() => set({ motive: 'both' })} testId="dz-motive-both">Both — compare everything</Radio>
                    <p className="mt-1 text-[13px] text-slate-600">
                        {/* A stated price is the price (25 Oct 2026): every plan buys at it; other prices are what-ifs. */}
                        {newHomePrice > 0 && housing !== 'rent'
                            ? `Every plan buys at the ${formatCurrency(newHomePrice)} named on step 1. A cheaper, a similar or a dearer home is shown as a what-if, never recommended.`
                            : a.motive === 'money'
                            ? 'A dearer home is shown as a what-if, never recommended: it would only hide more money in the home.'
                            : a.motive === 'home'
                                ? 'A similar and a dearer home compete as plans of their own, with the bills checked; a cheaper one is shown as a what-if.'
                                : 'Every price competes, from a cheaper home to a dearer one.'}
                    </p>
                </Q>
                <Q n={2} title="How many years should we plan for?" {...whyProps('years')} why={DZ_WHY_TEN} testId="dz-q-years">
                    <div className="flex flex-wrap items-center gap-2 text-[14px] text-slate-700">
                        <input type="number" min={1} max={30} value={a.years} onChange={(e) => set({ years: Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 1)) })} className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 text-center font-semibold text-slate-900" aria-label="Years to plan for" data-testid="dz-years" />
                        <span>years{a.years === 10 ? ', suggested for a move.' : '.'}</span>
                        {a.years !== 10 && <button type="button" onClick={() => set({ years: 10 })} className={`text-[13px] ${link}`}>Use 10</button>}
                    </div>
                </Q>
                <Q n={3} title="How much should stay in the bank for emergencies?" {...whyProps('reserve')} why={<>Money kept in the bank for the unexpected. We keep it back when we move {v.their} money, and draw on it only if a year runs short. Centrelink still counts it.</>} testId="dz-q-reserve">
                    <div className="flex flex-wrap items-center gap-3">
                        <CurrencyInput value={a.emergencyReserve} onChange={(x) => set({ emergencyReserve: Math.max(0, x ?? 0) })} className="w-36" ariaLabel="Money kept for emergencies" testId="dz-reserve" />
                        <span className="text-[13px] text-slate-500">
                            We suggest {formatCurrency(suggestedReserve)}: the greater of six months&apos; living costs and $50,000.
                            {Math.round(a.emergencyReserve) !== Math.round(suggestedReserve) && <> <button type="button" onClick={() => set({ emergencyReserve: suggestedReserve })} className={link}>Use {formatCurrency(suggestedReserve)}</button></>}
                        </span>
                    </div>
                    <p className="mt-1.5 text-[13.5px] text-slate-700">We keep it back when we move {v.their} money, and draw on it only if a year runs short. {v.Possessive} yearly costs are already on {v.their} file.</p>
                </Q>
                <Q n={4} title="A guaranteed income for life" testId="dz-q-annuity">
                    <GuaranteedIncomeQuestions
                        fromHomeSale
                        factFind={giView}
                        onGoals={(patch) => set({ guaranteedIncome: { ...(a.guaranteedIncome || {}), ...patch } })}
                        investable={investable}
                        investableLead={housing === 'rent' ? 'After the sale, renting instead of buying, and keeping the money for emergencies' : 'After the sale, the new home and its stamp duty, and keeping the money for emergencies'}
                        investableMadeOf="money freed by the sale, which the plans would invest"
                        inCare={guaranteedShapeAvailable(factFind as never)}
                        idPrefix="dz"
                    />
                    {!appetiteNone && <p className="mt-2 text-[13.5px] leading-relaxed text-slate-700"><strong className="text-slate-900">We work out how much.</strong> We test amounts up to a quarter of what {v.they} would invest, so most of {v.their} money stays within reach, and where it would lift the Age Pension, the amount that lifts it most.</p>}
                </Q>
                <Q n={5} title="How would money that is freed up be invested?" testId="dz-q-invest">
                    <select value={a.investProfile} onChange={(e) => set({ investProfile: e.target.value })} className="w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-[14px]" aria-label="How money that is freed up is invested" data-testid="dz-invest-profile">
                        <option value="">Not sure — assume the most cautious</option>
                        {INVESTMENT_PROFILE_IDS.map((id) => <option key={id} value={id}>{INVESTMENT_PROFILES[id].label}</option>)}
                    </select>
                    <p className="mt-1 text-[13px] text-slate-500">Saved to {v.possessive} profile, so it is asked once.</p>
                </Q>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <span className="text-[14px] text-slate-700">Prefer to set the numbers yourself? <button type="button" onClick={onBuildYourOwn} className={link} data-testid="dz-build-own">Build a plan yourself instead</button></span>
                <span className="flex items-center gap-3">
                    {!firstRun && onCancel && <button type="button" onClick={onCancel} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800">Cancel</button>}
                    <button type="button" onClick={() => onRun(a)} className="rounded-lg bg-blue-600 px-5 py-2.5 text-[15px] font-semibold text-white hover:bg-blue-700" data-testid="dz-run">{firstRun ? 'Test the ways to use the money' : 'Test the ways again'}</button>
                </span>
            </div>
        </div>
    );
};

export default DzQuestions;
